import { useCallback, useEffect, useState, Fragment } from "react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import api, { apiError } from "@/lib/api";
import { BulkPinDialog } from "@/components/BulkPinDialog";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users as UsersIcon, UserPlus, Trash2, ChevronDown, Pencil, KeyRound, ScanLine, CheckSquare, X, Link2, ShieldCheck, Eye, EyeOff, Loader2, RefreshCw, Clock, Layers, ArrowUp, ArrowDown, SlidersHorizontal, Mail, MessageCircle } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import { EntityAvatar, AvatarUpload } from "@/components/EntityAvatar";
import BulkDeleteDialog from "@/components/BulkDeleteDialog";
import NewStaffDialog from "@/components/NewStaffDialog";
import BulkUserImportDialog from "@/components/BulkUserImportDialog";
import { BankBadge } from "@/lib/banks";
import TaxPortalLookup from "@/components/TaxPortalLookup";
import PermissionTree from "@/components/PermissionTree";
import { DatePickerTR } from "@/components/DatePickerTR";

const RESTRICTED = ["admin", "assistant"];
const GLOBAL_ROLES = ["admin", "assistant"];
const STORE_MANAGER_GRANTS = ["assistant_manager", "cashier"];
const SHIFTS = [["morning", "Sabah"], ["noon", "Öğle"], ["evening", "Akşam"]];
const USER_COLS = [["username", "Kullanıcı Adı"], ["email", "E-posta"], ["role", "Rol"], ["status", "Durum"], ["location", "Lokasyon"], ["twofa", "2FA"], ["lastlogin", "Son Giriş"], ["pin", "PIN"]];
const emptyForm = { name: "", email: "", username: "", password: "", role: "cashier", pin: "", card_code: "", location_ids: [], phone: "", national_id: "", hire_date: "", avatar_url: "", personnel_id: "", sectors: [], tier: "", schedule_mode: "all_day", shifts: {}, is_shared_login: false, extra_permissions: [], denied_permissions: [], module_overrides: {} };

const fmtLastLogin = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function Users() {
  const { user: me, can } = useAuth();
  const isAdmin = me?.role === "admin";
  const canCreate = can("user.create") || can("user.manage");
  const canFull = canCreate; // tam kimlik düzenleme (ad/e-posta/PIN/kademe/yetki); yalnız user.assign olan sadece eşleştirir + rol/lokasyon atar
  const canAssign = can("user.assign") || can("user.manage");
  const canDelete = can("user.delete") || can("user.manage");
  const canSecurity = can("user.security") || can("user.manage");
  const myPerms = new Set(me?.permissions || []);
  const myAll = myPerms.has("*");
  const [cols, setCols] = useState(() => { try { return JSON.parse(localStorage.getItem("groot_user_cols")) || {}; } catch { return {}; } });
  const [colsOpen, setColsOpen] = useState(false);
  const col = (k) => (cols[k] !== undefined ? cols[k] : true);
  const toggleCol = (k) => setCols((c) => { const cur = c[k] !== undefined ? c[k] : true; const nc = { ...c, [k]: !cur }; try { localStorage.setItem("groot_user_cols", JSON.stringify(nc)); } catch (e) {} return nc; });
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [scanOpen, setScanOpen] = useState(false);
  const [pinPolicy, setPinPolicy] = useState({ critical_roles: [], critical_len: 6, default_len: 4 });
  const [rolePerms, setRolePerms] = useState({});
  const [permTree, setPermTree] = useState(null);
  const [roleVisMap, setRoleVisMap] = useState({});
  const [showPerms, setShowPerms] = useState(false);
  const [userTreeQuery, setUserTreeQuery] = useState("");
  const [sectorQuery, setSectorQuery] = useState("");
  const [locQuery, setLocQuery] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [showHrDetails, setShowHrDetails] = useState(false);   // İK/kişisel alanlar katlanır (rol-bazlı sadeleştirme)
  const [bulkImportOpen, setBulkImportOpen] = useState(false);   // toplu personel Excel aktarımı
  const [credsInfo, setCredsInfo] = useState(null);   // personel oluşturulunca üretilen giriş bilgileri
  const [twofa, setTwofa] = useState({ enabled: false, method: "email", forced: false });
  const [twofaBusy, setTwofaBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [userTiers, setUserTiers] = useState([]);
  const [tierMgrOpen, setTierMgrOpen] = useState(false);
  const [tierDraft, setTierDraft] = useState([]);
  const [tierBusy, setTierBusy] = useState(false);
  const filteredUsers = users.filter((u) => {
    const t = search.trim().toLocaleLowerCase("tr");
    if (t && !((u.name || "").toLocaleLowerCase("tr").includes(t) || (u.email || "").toLowerCase().includes(t) || (u.phone || "").includes(t) || (u.staff_code || "").toLowerCase().includes(t))) return false;
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter === "active" && u.is_active === false) return false;
    if (statusFilter === "passive" && u.is_active !== false) return false;
    if (tierFilter && u.tier !== tierFilter) return false;
    if (locFilter) {
      if (locFilter === "__global__") { if (!GLOBAL_ROLES.includes(u.role)) return false; }
      else {
        const lids = (u.location_ids && u.location_ids.length) ? u.location_ids : (u.location_id ? [u.location_id] : []);
        if (!lids.includes(locFilter)) return false;
      }
    }
    return true;
  });
  const tierRank = (u) => { const i = userTiers.findIndex((t) => t.code === u.tier); return i === -1 ? 9999 : i; };
  const tierLabelOf = (u) => (u && (u.tier_label || (userTiers.find((t) => t.code === u.tier) || {}).label)) || "Kademesiz";
  const sortedUsers = [...filteredUsers].sort((a, b) => (tierRank(a) - tierRank(b)) || (a.name || "").localeCompare(b.name || "", "tr"));
  const usersPaged = usePaged(sortedUsers);
  const [sectorOpts, setSectorOpts] = useState([]);
  useEffect(() => { api.get("/sectors").then((r) => setSectorOpts(r.data || [])).catch(() => {}); }, []);
  const toggleSector = (code) => setForm((f) => {
    const sectors = (f.sectors || []).includes(code) ? f.sectors.filter((x) => x !== code) : [...(f.sectors || []), code];
    const allowed = (id) => {
      if (!sectors.length) return true;
      const loc = locations.find((l) => l.id === id);
      const ls = loc?.sectors || [];
      return !ls.length || ls.some((c) => sectors.includes(c));
    };
    return { ...f, sectors, location_ids: (f.location_ids || []).filter(allowed) };
  });

  const [staffDir, setStaffDir] = useState([]);
  const [newStaffOpen, setNewStaffOpen] = useState(false);
  const [staffQuery, setStaffQuery] = useState("");
  const [staffOpen, setStaffOpen] = useState(false);
  const loadStaff = useCallback(() => api.get("/users/staff-directory").then((r) => setStaffDir(r.data || [])).catch(() => {}), []);
  const availableStaff = staffDir.filter((s) => !s.linked || s.id === form.personnel_id);
  const selectedStaff = staffDir.find((s) => s.id === form.personnel_id);
  const staffLockedRole = selectedStaff?.default_role || "";
  const selectPersonnel = (pid) => {
    const p = staffDir.find((s) => s.id === pid);
    setForm((f) => ({ ...f, personnel_id: pid, ...(p ? { name: p.name || "", national_id: p.national_id || "", phone: p.phone || "", hire_date: p.hire_date ? String(p.hire_date).slice(0, 10) : "", ...(p.default_role ? { role: p.default_role } : {}) } : {}) }));
  };
  const onStaffCreated = (p) => {
    const item = { id: p.id, code: p.code, name: p.name, national_id: p.national_id, phone: p.phone, email: p.email, hire_date: p.hire_date, bank: p.bank, title: p.title, default_role: p.default_role, linked: false };
    setStaffDir((d) => [item, ...d.filter((x) => x.id !== item.id)]);
    setForm((f) => ({ ...f, personnel_id: item.id, name: item.name || "", national_id: item.national_id || "", phone: item.phone || "", hire_date: item.hire_date ? String(item.hire_date).slice(0, 10) : "", ...(p.default_role ? { role: p.default_role } : {}) }));
    setNewStaffOpen(false);
  };

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  const [revealedPins, setRevealedPins] = useState({});
  const [revealBusy, setRevealBusy] = useState("");
  const revealPin = async (u) => {
    if (revealedPins[u.id]) { setRevealedPins((m) => { const n = { ...m }; delete n[u.id]; return n; }); return; }
    setRevealBusy(u.id);
    try {
      const { data } = await api.get(`/users/${u.id}/pin`);
      if (data.pin) setRevealedPins((m) => ({ ...m, [u.id]: data.pin }));
      else toast.info(data.message || "PIN görüntülenemiyor");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "PIN alınamadı"); }
    finally { setRevealBusy(""); }
  };
  const resetPin = async (u) => {
    if (!window.confirm(`${u.name || u.email} için yeni rastgele PIN atansın mı? (eski PIN geçersiz olur)`)) return;
    setRevealBusy(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/pin/reset`);
      setRevealedPins((m) => ({ ...m, [u.id]: data.pin }));
      load();
      toast.success(`Yeni PIN atandı: ${data.pin}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "PIN atanamadı"); }
    finally { setRevealBusy(""); }
  };
  useEffect(() => {
    load();
    loadStaff();
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/auth/pin-policy").then((r) => setPinPolicy(r.data)).catch(() => {});
    api.get("/user-tiers").then((r) => setUserTiers(r.data.tiers || [])).catch(() => {});
    api.get("/role-permissions").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.roles || []);
      const m = {}; arr.forEach((x) => { m[x.role] = x.permissions || []; }); setRolePerms(m);
    }).catch(() => {});
    if (can("role.manage")) {
      api.get("/permission-tree").then((r) => setPermTree(r.data)).catch(() => {});
      api.get("/role-module-visibility").then((r) => setRoleVisMap(r.data.map || {})).catch(() => {});
    }
  }, [can, load, loadStaff]);

  const isStoreManager = me?.role === "store_manager";
  const roleOptions = (currentRole) => {
    const base = roles.filter((r) => !r.disabled || r.code === currentRole);
    if (isAdmin) return base;
    if (me?.role === "assistant") return base.filter((r) => r.code !== "admin" || r.code === currentRole);
    if (isStoreManager) return base.filter((r) => STORE_MANAGER_GRANTS.includes(r.code) || r.code === currentRole);
    return base.filter((r) => !RESTRICTED.includes(r.code) || r.code === currentRole);
  };

  const isGlobalRole = (role) => GLOBAL_ROLES.includes(role);
  const reqPinLen = pinPolicy.critical_roles.includes(form.role) ? pinPolicy.critical_len : pinPolicy.default_len;
  const scheduleMode = form.schedule_mode || "all_day";
  const roleBase = rolePerms[form.role] || [];
  // Rol POS kullanıyor mu? (kasa/satış) → sadece bu rollerde POS PIN + Kart Kodu göster
  const usesPOS = !isGlobalRole(form.role) && (roleBase.includes("*") || roleBase.includes("sale.create") || roleBase.includes("sale.discount"));
  const toggleExtra = (code) => setForm((f) => ({ ...f, extra_permissions: f.extra_permissions.includes(code) ? f.extra_permissions.filter((x) => x !== code) : [...f.extra_permissions, code] }));
  const toggleDenied = (code) => setForm((f) => ({ ...f, denied_permissions: f.denied_permissions.includes(code) ? f.denied_permissions.filter((x) => x !== code) : [...f.denied_permissions, code] }));
  const setModOverride = (key, val) => setForm((f) => {
    const m = { ...(f.module_overrides || {}) };
    if (val === null) delete m[key]; else m[key] = val;
    return { ...f, module_overrides: m };
  });
  const roleHiddenSet = new Set(roleVisMap[form.role] || []);
  const visSeg = (key, label) => {
    const inheritHidden = roleHiddenSet.has(key);
    const ov = form.module_overrides || {};
    const st = key in ov ? (ov[key] ? "show" : "hide") : "default";
    const opts = [["default", inheritHidden ? "Varsayılan · gizli" : "Varsayılan"], ["show", "Göster"], ["hide", "Gizle"]];
    return (
      <div className="flex items-center justify-between gap-2">
        {label && <span className="text-xs text-slate-600">{label}</span>}
        <div className="inline-flex overflow-hidden rounded-md text-[10px] font-semibold ring-1 ring-inset ring-slate-200" data-testid={`user-vis-${key === "/" ? "panel" : key.replace(/[/#]+/g, "-").replace(/^-+/, "")}`}>
          {opts.map(([v, lbl]) => {
            const on = st === v;
            const cls = on ? (v === "hide" ? "bg-red-500 text-white" : v === "show" ? "bg-emerald-500 text-white" : "bg-slate-600 text-white") : "bg-white text-slate-500 hover:bg-slate-50";
            return <button key={v} type="button" onClick={() => setModOverride(key, v === "default" ? null : v === "show")} className={`px-2 py-1 transition-colors ${cls}`}>{lbl}</button>;
          })}
        </div>
      </div>
    );
  };
  const permSeg = (p) => {
    const inRole = roleBase.includes(p.code);
    const granted = form.extra_permissions.includes(p.code);
    const denied = form.denied_permissions.includes(p.code);
    const effective = inRole ? !denied : granted;
    return (
      <div data-testid={`perm-row-${p.code}`} className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-inset ring-slate-100">
        <span className={`text-xs ${effective ? "text-slate-700" : "text-slate-400 line-through"}`}>{p.label}</span>
        {inRole ? (
          <button type="button" data-testid={`perm-deny-${p.code}`} onClick={() => toggleDenied(p.code)}
            className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${denied ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
            {denied ? "Kısıtlandı" : "Rolde var"}
          </button>
        ) : (!myAll && !myPerms.has(p.code)) ? (
          <span data-testid={`perm-locked-${p.code}`} title="Bu yetkiye kendiniz sahip olmadığınız için başkasına veremezsiniz"
            className="shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-300">Yetkiniz yok</span>
        ) : (
          <button type="button" data-testid={`perm-grant-${p.code}`} onClick={() => toggleExtra(p.code)}
            className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${granted ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {granted ? "Ek yetki ✓" : "Ver"}
          </button>
        )}
      </div>
    );
  };

  const toggleLoc = (id) => setForm((f) => ({
    ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id],
  }));
  const locationOptions = (isStoreManager
    ? (me?.locations || []).map((l) => ({ id: l.id, name: l.name, type_label: "", sectors: l.sectors || [] }))
    : locations.filter((l) => l.is_active !== false)
  ).filter((l) => {
    const fs = form.sectors || [];
    if (!fs.length) return true;                 // sektör seçilmedi → tüm lokasyonlar
    const ls = l.sectors || [];
    return !ls.length || ls.some((c) => fs.includes(c));  // sektörsüz lokasyon evrensel; yoksa kesişim
  });
  const allLocIds = locationOptions.map((l) => l.id);
  const isAllLoc = allLocIds.length > 0 && allLocIds.every((id) => form.location_ids.includes(id));
  const toggleAllLoc = () => setForm((f) => ({ ...f, location_ids: isAllLoc ? [] : allLocIds }));

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, role: "cashier", location_ids: isStoreManager ? (me?.location_ids || []) : [] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, username: u.username || "", password: "", role: u.role, pin: "", card_code: u.card_code || "", location_ids: u.location_ids || (u.location_id ? [u.location_id] : []), phone: u.phone || "", national_id: u.national_id || "", hire_date: u.hire_date || "", avatar_url: u.avatar_url || "", personnel_id: u.personnel_id || "", sectors: u.sectors || [], tier: u.tier || "", schedule_mode: u.schedule_mode || "all_day", shifts: u.shifts ? Object.fromEntries(Object.entries(u.shifts).map(([k, v]) => [k, { personnel_id: v.personnel_id || "", active: v.active !== false, password: "", pin: "" }])) : {}, is_shared_login: !!u.is_shared_login, extra_permissions: u.extra_permissions || [], denied_permissions: u.denied_permissions || [], module_overrides: u.module_overrides || {} });
    setTwofa({ enabled: !!u.twofa_enabled, method: u.twofa_method || "email", forced: !!u.twofa_forced });
    setOpen(true);
  };

  const methodLabel = (m) => ({ email: "E-posta", sms: "SMS", both: "E-posta + SMS" }[m] || "E-posta");
  const applyTwofa = async (next) => {
    if (!editing) return;
    setTwofaBusy(true);
    try {
      const { data } = await api.post(`/users/${editing.id}/2fa`, {
        action: next.enabled ? "on" : "off", method: next.method, forced: next.forced });
      setTwofa({ enabled: data.twofa_enabled, method: data.twofa_method || "email", forced: !!data.twofa_forced });
      toast.success("2FA ayarı güncellendi");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTwofaBusy(false); }
  };
  const sendResetEmail = async () => {
    if (!editing) return;
    if (!window.confirm(`${editing.name || editing.email} kullanıcısına şifre sıfırlama e-postası gönderilsin mi? Kullanıcı e-postadaki adımlarla kendi yeni şifresini belirleyecek ve mevcut oturumları sonlanacak.`)) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/users/${editing.id}/send-reset-email`);
      toast.success(`Şifre sıfırlama e-postası gönderildi: ${data.email}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "E-posta gönderilemedi"); }
    finally { setResetBusy(false); }
  };

  const openTierMgr = () => { setTierDraft(userTiers.map((t) => ({ ...t }))); setTierMgrOpen(true); };
  const saveTiers = async () => {
    const clean = tierDraft.filter((t) => (t.label || "").trim());
    if (!clean.length) return toast.error("En az bir kademe tanımlayın");
    setTierBusy(true);
    try {
      const { data } = await api.put("/user-tiers", { tiers: clean.map((t) => ({ code: t.code || "", label: t.label.trim() })) });
      setUserTiers(data.tiers || []);
      toast.success("Kademeler güncellendi");
      setTierMgrOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setTierBusy(false); }
  };
  const canManageTiers = can("role.manage") || can("user.manage");
  const moveTier = (i, dir) => setTierDraft((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.length) return d;
    const next = [...d];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    if (editing) {
      if (canFull) {
        if (!form.name.trim()) return toast.error("Ad Soyad zorunlu");
        if (!form.username?.trim() && (!form.email.trim() || !form.email.includes("@"))) return toast.error("Geçerli bir e-posta veya kullanıcı adı zorunlu");
        if (scheduleMode !== "shift" && form.pin && form.pin.trim().length !== reqPinLen) return toast.error(`POS PIN ${reqPinLen} haneli olmalı`);
        if (!isGlobalRole(form.role) && form.location_ids.length === 0) return toast.error("Bu rol için en az bir lokasyon seçin");
      }
      if (scheduleMode === "shift") {
        const filled = Object.entries(form.shifts || {}).filter(([, v]) => v && v.personnel_id);
        if (filled.length === 0) return toast.error("En az bir vardiya (Sabah/Öğle/Akşam) için personel seçin");
      }
      try {
        const payload = {};
        if (canFull) { payload.role = form.role; payload.location_ids = isGlobalRole(form.role) ? [] : form.location_ids; }
        if (scheduleMode === "shift") { payload.schedule_mode = "shift"; payload.shifts = form.shifts; }
        else { payload.personnel_id = form.personnel_id || null; if (canFull) payload.schedule_mode = "all_day"; }
        if (canFull) Object.assign(payload, {
          name: form.name.trim(), email: form.email.trim() || undefined, username: (form.username || "").trim(), avatar_url: form.avatar_url,
          sectors: form.sectors, tier: form.tier,
          extra_permissions: form.extra_permissions, denied_permissions: form.denied_permissions,
          module_overrides: form.module_overrides,
          is_shared_login: !!form.is_shared_login,
          ...(scheduleMode === "shift"
            ? {}
            : { password: form.password || undefined, pin: form.pin || undefined,
                card_code: form.card_code || undefined, phone: form.phone || undefined,
                national_id: form.national_id || undefined, hire_date: form.hire_date || undefined }),
        });
        const { data } = await api.put(`/users/${editing.id}`, payload);
        toast.success(data?.pending_approval ? (data.message || "Değişiklik onaya gönderildi") : "Kullanıcı güncellendi");
        setOpen(false); load(); loadStaff();
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
      return;
    }
    if (scheduleMode === "shift") {
      if (!form.name.trim() || (!form.email.trim() && !form.username?.trim())) return toast.error("Slot adı ve (e-posta veya kullanıcı adı) zorunlu");
      const shifts = form.shifts || {};
      const filled = Object.entries(shifts).filter(([, v]) => v && v.personnel_id);
      if (filled.length === 0) return toast.error("En az bir vardiya (Sabah/Öğle/Akşam) için personel seçin");
      for (const [, v] of filled) {
        if (!v.password) return toast.error("Her dolu vardiya için şifre girin");
        if (!v.pin || v.pin.length !== reqPinLen) return toast.error(`Vardiya PIN ${reqPinLen} haneli olmalı`);
      }
      if (!isGlobalRole(form.role) && form.location_ids.length === 0) return toast.error("Bu rol için en az bir lokasyon seçin");
      try {
        const { data } = await api.post("/users", {
          email: form.email.trim() || undefined, username: form.username?.trim() || undefined, name: form.name.trim(), role: form.role, schedule_mode: "shift",
          shifts, location_ids: isGlobalRole(form.role) ? [] : form.location_ids,
          tier: form.tier, sectors: form.sectors, avatar_url: form.avatar_url,
          extra_permissions: form.extra_permissions, denied_permissions: form.denied_permissions, module_overrides: form.module_overrides,
        });
        toast.success(data?.pending_approval ? (data.message || "Kayıt onaya gönderildi") : "Vardiyalı kullanıcı oluşturuldu");
        setOpen(false); setForm(emptyForm); load(); loadStaff();
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
      return;
    }
    if (!isAdmin && !form.personnel_id) return toast.error("Önce bir personel eşleştirin (Personel Eşleştir)");
    if (!form.name.trim()) return toast.error("Ad Soyad zorunlu");
    if (form.pin && form.pin.trim().length !== reqPinLen) return toast.error(`POS PIN ${reqPinLen} haneli olmalı (boş bırakırsanız otomatik üretilir)`);
    if (!isAdmin) {
      if (!form.phone.trim() || !isValidPhone(form.phone) || form.national_id.trim().length !== 11 || !form.hire_date) setShowHrDetails(true);
      if (!form.phone.trim()) return toast.error("Cep telefonu zorunlu");
      if (!isValidPhone(form.phone)) return toast.error("Telefon numarası geçersiz — ülke koduna göre hane sayısını kontrol edin");
      if (form.national_id.trim().length !== 11) return toast.error("TC Kimlik No 11 haneli olmalı");
      if (!form.hire_date) return toast.error("İşe giriş tarihi zorunlu");
    }
    if (!isGlobalRole(form.role) && form.location_ids.length === 0) return toast.error("Bu rol için en az bir lokasyon seçin");
    try {
      const { data } = await api.post("/users", {
        ...form, name: form.name.trim(), email: form.email.trim(),
        location_ids: isGlobalRole(form.role) ? [] : form.location_ids,
      });
      if (data?.pending_approval) {
        toast.success(data.message || "Kullanıcı kaydı onaya gönderildi");
      } else {
        toast.success("Kullanıcı oluşturuldu");
        if (data?.username) setCredsInfo({ name: form.name.trim(), username: data.username, password: data.temp_password || form.password || null, pin: data.temp_pin || null, email: (form.email || "").trim() || null, phone: (form.phone || "").trim() || null });
      }
      setOpen(false); setForm(emptyForm); load(); loadStaff();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Bu kullanıcı silinsin mi?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const roleColor = (r) => ({
    admin: "bg-brand-green text-white", assistant: "bg-emerald-100 text-emerald-700",
    cashier: "bg-sky-100 text-sky-700", cashier_chief: "bg-sky-100 text-sky-800",
    stock_officer: "bg-orange-100 text-orange-700", store_manager: "bg-purple-100 text-purple-700",
    assistant_manager: "bg-purple-100 text-purple-600", price_officer: "bg-amber-100 text-amber-700",
  }[r] || "bg-slate-100 text-slate-600");

  // lower roles may not edit admin/assistant users at all
  const canEditUser = (u) => canAssign && (isAdmin || !RESTRICTED.includes(u.role));
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPinOpen, setBulkPinOpen] = useState(false);
  const deletable = (u) => u.role !== "admin" && u.id !== me?.id && canDelete && canEditUser(u);
  const toggleSel = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };
  const selectableUsers = usersPaged.slice.filter(deletable);
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((u) => selectedIds.includes(u.id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableUsers.map((u) => u.id));
  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} kullanıcı silindi${data.skipped ? ` · ${data.skipped} atlandı` : ""}`);
      setBulkOpen(false); exitSelect(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Toplu silme başarısız"); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-4 fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Yönetim</p><h1 className="font-heading text-2xl font-bold text-slate-900">Kullanıcılar</h1></div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canDelete && (
            <Button data-testid="users-select-toggle" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} variant="outline"
              className={selectMode ? "border-2 border-red-500 bg-red-50 text-red-700 shadow-sm hover:bg-red-100" : "border-2 border-red-200 bg-white text-red-600 shadow-sm hover:border-red-300 hover:bg-red-50"}>
              {selectMode ? <><X className="mr-2 h-4 w-4" /> Seçimi Kapat</> : <><CheckSquare className="mr-2 h-4 w-4" /> Seç / Sil</>}
            </Button>
          )}
          {isAdmin && (
            <Button data-testid="bulk-pin-btn" onClick={() => setBulkPinOpen(true)} variant="outline"
              className="border-sky-300 text-sky-700 hover:bg-sky-50">
              <KeyRound className="mr-2 h-4 w-4" /> Toplu PIN
            </Button>
          )}
          {canManageTiers && (
            <Button data-testid="tiers-manage-header-btn" onClick={openTierMgr} variant="outline"
              className="border-brand-green/30 text-brand-green hover:bg-emerald-50">
              <Layers className="mr-2 h-4 w-4" /> Kademeler
            </Button>
          )}
          <div className="relative">
            <Button data-testid="users-cols-btn" onClick={() => setColsOpen((o) => !o)} variant="outline" className="border-slate-300">
              <SlidersHorizontal className="mr-2 h-4 w-4" /> Kolonlar
            </Button>
            {colsOpen && (<>
              <div className="fixed inset-0 z-20" onClick={() => setColsOpen(false)} />
              <div data-testid="users-cols-menu" className="absolute right-0 z-30 mt-1 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                <p className="px-2 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Görünen Kolonlar</p>
                {USER_COLS.map(([k, lbl]) => (
                  <label key={k} data-testid={`users-col-${k}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input type="checkbox" checked={col(k)} onChange={() => toggleCol(k)} className="h-4 w-4 accent-brand-green" /> {lbl}
                  </label>
                ))}
              </div>
            </>)}
          </div>
          {canCreate && (
            <Button data-testid="bulk-import-btn" onClick={() => setBulkImportOpen(true)} variant="outline" className="border-brand-green text-brand-green hover:bg-emerald-50">
              <UserPlus className="mr-2 h-4 w-4" /> Toplu Aktar
            </Button>
          )}
          {canCreate && (
            <Button data-testid="add-user-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
              <UserPlus className="mr-2 h-4 w-4" /> Yeni Kullanıcı
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="users-kpis">
        <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200"><p className="text-xs text-slate-500">Toplam Kullanıcı</p><p className="mt-1 text-2xl font-bold text-slate-800">{users.length}</p></div>
        <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200"><p className="text-xs text-slate-500">Aktif</p><p className="mt-1 text-2xl font-bold text-emerald-600">{users.filter((u) => u.is_active !== false).length}</p></div>
        <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200"><p className="text-xs text-slate-500">2FA Açık</p><p className="mt-1 text-2xl font-bold text-sky-600">{users.filter((u) => u.twofa_enabled).length}</p></div>
        <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200"><p className="text-xs text-slate-500">PIN Eksik</p><p className="mt-1 text-2xl font-bold text-amber-600">{users.filter((u) => !u.has_pin).length}</p></div>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="users-filters">
        <Input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, e-posta, telefon, kod ara…" className="h-9 w-full sm:w-72" />
        <select data-testid="users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">Tüm roller</option>
          {roles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
        <select data-testid="users-tier-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">Tüm kademeler</option>
          {userTiers.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
        <select data-testid="users-location-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">Tüm lokasyonlar</option>
          <option value="__global__">Global (tüm lokasyonlar)</option>
          {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select data-testid="users-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="all">Hepsi</option><option value="active">Aktif</option><option value="passive">Pasif</option>
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="users-filter-count">{filteredUsers.length} / {users.length} kullanıcı</span>
      </div>

      {selectMode && (
        <div data-testid="users-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-md bg-red-50 px-3 py-2 ring-1 ring-inset ring-red-200">
          <span className="text-sm font-semibold text-red-700" data-testid="users-selected-count">{selectedIds.length} seçili</span>
          <Button size="sm" variant="outline" data-testid="users-select-all" onClick={toggleAll} className="h-8 border-slate-300">{allSelected ? "Sayfadakileri bırak" : "Sayfadakileri seç"}</Button>
          <Button size="sm" data-testid="users-bulk-delete" disabled={selectedIds.length === 0} onClick={() => setBulkOpen(true)} className="ml-auto h-8 bg-red-600 text-white hover:bg-red-700">
            <Trash2 className="mr-1.5 h-4 w-4" /> Seçili {selectedIds.length} Kullanıcıyı Sil
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              {selectMode && <th className="px-3 py-3"><input type="checkbox" data-testid="users-select-all-head" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-red-600" /></th>}
              <th className="px-4 py-3 font-medium">Ad</th>
              {col("username") && <th className="px-4 py-3 font-medium">Kullanıcı Adı</th>}
              {col("email") && <th className="px-4 py-3 font-medium">E-posta</th>}
              {col("role") && <th className="px-4 py-3 font-medium">Rol</th>}
              {col("status") && <th className="px-4 py-3 font-medium">Durum</th>}
              {col("location") && <th className="px-4 py-3 font-medium">Lokasyon</th>}
              {col("twofa") && <th className="px-4 py-3 font-medium">2FA</th>}
              {col("lastlogin") && <th className="px-4 py-3 font-medium">Son Giriş</th>}
              {col("pin") && <th className="px-4 py-3 font-medium">PIN</th>}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {usersPaged.slice.map((u, _i) => {
              const _showTier = _i === 0 || tierLabelOf(usersPaged.slice[_i - 1]) !== tierLabelOf(u);
              return (
              <Fragment key={u.id}>
              {_showTier && (
                <tr data-testid={`user-tier-group-${u.tier || "none"}`} className="bg-slate-100/80">
                  <td colSpan={1 + USER_COLS.filter(([k]) => col(k)).length + 1 + (selectMode ? 1 : 0)} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    <span className="inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-brand-green" /> {tierLabelOf(u)}</span>
                  </td>
                </tr>
              )}
              <tr data-testid={`user-row-${u.id}`} className={`hover:bg-slate-50 ${selectMode && selectedIds.includes(u.id) ? "bg-red-50/60" : ""}`}>
                {selectMode && <td className="px-3 py-3">{deletable(u) ? <input type="checkbox" data-testid={`user-select-${u.id}`} checked={selectedIds.includes(u.id)} onChange={() => toggleSel(u.id)} className="h-4 w-4 accent-red-600" /> : <span className="text-[10px] text-slate-300">—</span>}</td>}
                <td className="px-4 py-3 font-medium text-slate-800">
                  <div className="flex items-center gap-2.5">
                    <EntityAvatar src={u.avatar_url} name={u.name} size={34} />
                    <span>{u.name}{u.staff_code && <span className="ml-2 font-mono text-[10px] text-slate-400">{u.staff_code}</span>}</span>
                  </div>
                </td>
                {col("username") && <td className="px-4 py-3 text-slate-500" data-testid={`user-username-${u.id}`}>{u.username || <span className="text-slate-300">—</span>}</td>}
                {col("email") && <td className="px-4 py-3 text-slate-500">
                  <p>{u.email}</p>
                  {u.phone && <p className="text-[11px] text-slate-400">{u.phone}</p>}
                </td>}
                {col("role") && <td className="px-4 py-3"><span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${roleColor(u.role)}`}>{u.role_label}</span>{u.schedule_mode === "shift" && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-sky-600" data-testid={`user-schedule-${u.id}`} title={u.shift_summary || ""}><Clock className="h-3 w-3" /> Vardiyalı</div>}</td>}
                {col("status") && <td className="px-4 py-3" data-testid={`user-status-${u.id}`}>
                  {u.is_active === false
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Pasif</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Aktif</span>}
                </td>}
                {col("location") && <td className="px-4 py-3 text-slate-600" data-testid={`user-location-${u.id}`}>
                  {isGlobalRole(u.role)
                    ? <span className="text-[11px] font-medium text-slate-400">Global (tüm lokasyonlar)</span>
                    : (u.location_names && u.location_names.length
                        ? <span className="text-[12px]">{u.location_names.join(", ")}</span>
                        : <span className="text-[12px]">{u.location_name || <span className="text-red-500">atanmadı</span>}</span>)}
                </td>}
                {col("twofa") && <td className="px-4 py-3" data-testid={`user-2fa-${u.id}`}>
                  {u.twofa_enabled
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600"><ShieldCheck className="h-3.5 w-3.5" /> {methodLabel(u.twofa_method)}{u.twofa_forced ? " · Zorunlu" : ""}</span>
                    : <span className="text-[11px] text-slate-400">Kapalı</span>}
                </td>}
                {col("lastlogin") && <td className="px-4 py-3 text-[11px] text-slate-500" data-testid={`user-lastlogin-${u.id}`}>{fmtLastLogin(u.last_login)}</td>}
                {col("pin") && <td className="px-4 py-3">
                  {u.has_pin ? (
                    <div className="flex items-center gap-1.5">
                      {isAdmin && revealedPins[u.id]
                        ? <span data-testid={`user-pin-value-${u.id}`} className="tabular font-semibold tracking-widest text-slate-800">{revealedPins[u.id]}</span>
                        : <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"><KeyRound className="h-3 w-3" /> tanımlı</span>}
                      {isAdmin && (<>
                        <button type="button" data-testid={`user-reveal-pin-${u.id}`} onClick={() => revealPin(u)} disabled={revealBusy === u.id}
                          title={revealedPins[u.id] ? "Gizle" : "PIN göster"}
                          className="rounded-sm p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          {revealBusy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : revealedPins[u.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <button type="button" data-testid={`user-reset-pin-${u.id}`} onClick={() => resetPin(u)} disabled={revealBusy === u.id}
                          title="Yeni rastgele PIN ata (eski PIN'i görünür kılar)"
                          className="rounded-sm p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-600">
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                      </>)}
                    </div>
                  ) : <span className="text-[11px] text-red-500">eksik</span>}
                </td>}
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {canEditUser(u) && (
                      <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                    )}
                    {u.role !== "admin" && u.id !== me?.id && canDelete && (
                      <button data-testid={`user-delete-${u.id}`} onClick={() => del(u.id)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <Pagination page={usersPaged.page} pageSize={usersPaged.pageSize} total={usersPaged.total}
          onPage={usersPaged.setPage} onPageSize={usersPaged.setPageSize} testid="users-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
            <DialogDescription>
              {editing ? "Şifre ve PIN alanını boş bırakırsanız değişmez." : `Tüm alanlar zorunludur. POS PIN ${reqPinLen} haneli olmalıdır.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div data-testid="user-role-top">
              <Label>Rol *</Label>
              {canFull && !staffLockedRole ? (
                <>
                  <div className="relative mt-1">
                    <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                      {roleOptions(editing?.role).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                  {isStoreManager && <p className="mt-1 text-[11px] text-slate-400">Yalnızca kendi mağazanız için alt roller ekleyebilirsiniz.</p>}
                  {!isAdmin && !isStoreManager && <p className="mt-1 text-[11px] text-slate-400">Yönetici ve Yönetici Asistanı rolünü yalnızca Yönetici atayabilir.</p>}
                  <p className="mt-1 text-[11px] text-brand-green">Rolü seçin — form yalnızca bu rol için gereken alanları gösterir.</p>
                </>
              ) : (
                <div data-testid="user-role-readonly" className="mt-1 rounded-md bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
                  {(roles.find((r) => r.code === form.role) || {}).label || form.role} <span className="ml-1 text-[11px] font-normal text-slate-400">{staffLockedRole ? "(personel görevinden geldi — sabit)" : "(sabit — değiştiremezsiniz)"}</span>
                </div>
              )}
            </div>
            {canFull && !isGlobalRole(form.role) && (
              <div data-testid="user-schedule-toggle" className="flex items-center gap-2 rounded-lg bg-slate-50 p-1 ring-1 ring-inset ring-slate-200">
                {[["all_day", "Tüm Zaman"], ["shift", "Vardiyalı (Sabah/Öğle/Akşam)"]].map(([m, lbl]) => (
                  <button key={m} type="button" data-testid={`schedule-mode-${m}`} onClick={() => setForm((f) => ({ ...f, schedule_mode: m }))}
                    className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${scheduleMode === m ? "bg-brand-green text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}>{lbl}</button>
                ))}
              </div>
            )}
            {scheduleMode === "shift" && (canFull || editing) && (
              <div className="space-y-3" data-testid="shift-user-block">
                <div className="rounded-md bg-sky-50 px-3 py-2 text-[11px] text-sky-700 ring-1 ring-inset ring-sky-200">Vardiyalı slot: her vardiya <b>AYRI personel + AYRI şifre + AYRI PIN</b> ile giriş yapar. En az bir vardiyayı doldurun. (Ör. aynı kasayı Sabah X, Akşam Y kullanır.)</div>
                {canFull ? (<>
                  <div><Label>Slot Adı *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Örn. Kasa 1 Görevlisi / Müdür Yrd." /></div>
                  <div><Label>E-posta (ortak giriş) {form.username?.trim() ? "(ops.)" : "*"}</Label><Input data-testid="user-email" type="email" value={form.email} autoComplete="off" onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                  <div><Label>Kullanıcı Adı <span className="text-slate-400">(ops.)</span></Label><Input data-testid="user-username" value={form.username} autoComplete="off" onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s+/g, "").toLowerCase() })} className="mt-1" placeholder="ör. kasa1" /></div>
                </>) : (
                  <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-inset ring-slate-200" data-testid="shift-slot-info">Slot: <b>{form.name}</b> · {form.email} <span className="text-slate-400">(sabit — yalnız vardiya personelini atarsınız)</span></div>
                )}
                {SHIFTS.map(([sk, lbl]) => (
                  <ShiftSlotEditor key={sk} sk={sk} label={lbl} value={(form.shifts || {})[sk]} staffDir={staffDir} reqPinLen={reqPinLen} editing={!!editing}
                    onChange={(nv) => setForm((f) => ({ ...f, shifts: { ...(f.shifts || {}), [sk]: nv } }))} />
                ))}
              </div>
            )}
            {scheduleMode === "all_day" && (
              <div className="rounded-xl border border-brand-green/30 bg-brand-green/5 p-3" data-testid="user-staff-mapping">
                <div className="mb-2 flex items-center justify-between">
                  <Label className="mb-0 flex items-center gap-1.5 font-semibold text-brand-green"><Link2 className="h-4 w-4" /> Personel {editing ? "Getir / Eşleştir" : "Eşleştir *"}</Label>
                  {canFull && <Button type="button" size="sm" variant="outline" data-testid="user-new-staff-btn" onClick={() => setNewStaffOpen(true)}
                    className="h-7 border-brand-green/40 text-brand-green hover:bg-brand-green/10"><UserPlus className="mr-1 h-3.5 w-3.5" /> Yeni Personel</Button>}
                </div>
                <div className="relative">
                  <Input data-testid="user-personnel-search"
                    value={selectedStaff ? `${selectedStaff.code} — ${selectedStaff.name}` : staffQuery}
                    onChange={(e) => { setStaffQuery(e.target.value); setForm((f) => ({ ...f, personnel_id: "" })); setStaffOpen(true); }}
                    onFocus={() => setStaffOpen(true)}
                    onBlur={() => setTimeout(() => setStaffOpen(false), 180)}
                    placeholder="Personel adı veya kodu ara…" autoComplete="off" />
                  {staffOpen && (
                    <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="user-personnel-options">
                      {availableStaff
                        .filter((s) => { const t = (staffQuery || "").toLowerCase().trim(); return !t || (s.name || "").toLowerCase().includes(t) || (s.code || "").toLowerCase().includes(t); })
                        .slice(0, 50)
                        .map((s) => (
                          <button key={s.id} type="button" data-testid={`personnel-opt-${s.id}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { selectPersonnel(s.id); setStaffQuery(""); setStaffOpen(false); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-brand-green/5">
                            <span className="font-mono text-xs text-slate-400">{s.code}</span>
                            <span className="font-medium text-slate-700">{s.name}</span>
                          </button>
                        ))}
                      {availableStaff.filter((s) => { const t = (staffQuery || "").toLowerCase().trim(); return !t || (s.name || "").toLowerCase().includes(t) || (s.code || "").toLowerCase().includes(t); }).length === 0 && (
                        <p className="px-3 py-2 text-xs text-slate-400">Eşleşen personel yok. "Yeni Personel" ile ekleyin.</p>
                      )}
                    </div>
                  )}
                </div>
                {selectedStaff ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="user-selected-staff">
                    <span className="font-mono text-slate-400">{selectedStaff.code}</span>
                    <span className="font-semibold text-slate-700">{selectedStaff.name}</span>
                    {selectedStaff.bank && <BankBadge code={selectedStaff.bank} />}
                  </div>
                ) : (
                  <p className="mt-1.5 text-[11px] text-slate-500">Kullanıcı bir personel kaydına bağlanmalıdır. Listede yoksa "Yeni Personel" ile ekleyin.</p>
                )}
              </div>
            )}
            {canFull && scheduleMode === "all_day" && (<>
            <div><Label>Ad Soyad *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>E-posta <span className="text-slate-400">(şifre sıfırlama için önerilir)</span></Label><Input data-testid="user-email" type="email" value={form.email} autoComplete="off"
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Kullanıcı Adı <span className="text-slate-400">(boş → sistem üretir)</span></Label>
              <Input data-testid="user-username" value={form.username} autoComplete="off"
                onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s+/g, "").toLowerCase() })} className="mt-1" placeholder="Boş bırakın — otomatik oluşturulur" />
              <p className="mt-1 text-[11px] text-slate-400">Personel <b>Firma + Kullanıcı Adı + Şifre</b> ile giriş yapar. Boş bırakırsanız isimden otomatik üretilir.</p></div>
            <div className={usesPOS ? "grid grid-cols-2 gap-3" : ""}>
              <div><Label>Şifre {editing ? "(ops.)" : <span className="text-slate-400">(boş → otomatik)</span>}</Label>
                <Input data-testid="user-password" type="text" value={form.password} autoComplete="new-password"
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1"
                  placeholder={editing ? "Değiştirmek için yazın" : "Boş → sistem üretir"} /></div>
              {usesPOS && (
              <div><Label>POS PIN {editing ? "(ops.)" : <span className="text-slate-400">(boş → otomatik)</span>} · {reqPinLen} hane</Label>
                <Input data-testid="user-pin" inputMode="numeric" maxLength={reqPinLen} value={form.pin} autoComplete="new-password"
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, reqPinLen) })} className="mt-1 tabular"
                  placeholder={`${reqPinLen} hane`} />
                <p className="mt-1 text-[10px] text-slate-400">{pinPolicy.critical_roles.includes(form.role) ? "Kritik/yönetim rolü — 6 hane zorunlu." : "Standart rol — 4 hane."}</p></div>
              )}
            </div>
            {usesPOS && (
            <div>
              <Label>Kart Kodu (ops.)</Label>
              <div className="mt-1 flex gap-2">
                <Input data-testid="user-card" value={form.card_code} autoComplete="off" onChange={(e) => setForm({ ...form, card_code: e.target.value })} className="font-mono" placeholder="Kartı okutun veya elle yazın" />
                <Button type="button" data-testid="user-card-scan" onClick={() => setScanOpen(true)} variant="outline" className="shrink-0 border-slate-300">
                  <ScanLine className="mr-1 h-4 w-4" /> Okut
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Termal/el terminali (USB), kamera veya elle girilebilir. POS'ta yetki onayı için kullanılır.</p>
            </div>
            )}
            {usesPOS && (
            <label className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="user-shared-login-row">
              <input type="checkbox" data-testid="user-shared-login" checked={!!form.is_shared_login} onChange={(e) => setForm({ ...form, is_shared_login: e.target.checked })} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
              <span className="text-sm text-slate-700"><b className="text-indigo-700">Ortak Rol Girişi</b> — bu hesabı birden çok personel paylaşır. Vardiya açma/kapatma ve hassas işlemlerde kişi <b>kendi Personel PIN'iyle</b> kimliğini belirtir (Z-raporunda gerçek kişi görünür).</span>
            </label>
            )}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="user-hr-details">
              <button type="button" data-testid="user-hr-toggle" onClick={() => setShowHrDetails((s) => !s)} className="flex w-full items-center justify-between text-sm font-semibold text-slate-700">
                <span>Kişisel / İK Bilgileri <span className="text-[11px] font-normal text-slate-400">(opsiyonel — personel kartından otomatik gelir)</span></span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showHrDetails ? "rotate-180" : ""}`} />
              </button>
              {showHrDetails && (
              <div className="mt-3 space-y-3">
                <div className="flex justify-center pb-1"><AvatarUpload value={form.avatar_url} name={form.name} onChange={(url) => setForm({ ...form, avatar_url: url })} size={72} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Cep Telefonu {isAdmin ? "(ops.)" : "*"}</Label><div className="mt-1"><PhoneInput testid="user-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></div></div>
                  <div><Label>TC Kimlik No {isAdmin ? "(ops.)" : "*"}</Label><Input data-testid="user-tckn" inputMode="numeric" maxLength={11} value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value.replace(/\D/g, "") })} className="mt-1 tabular" placeholder="11 hane" /></div>
                  <TaxPortalLookup taxNo={form.national_id} type="individual" mode="validate" testid="user-tckn-portal" />
                </div>
                <div><Label>İşe Giriş Tarihi {isAdmin ? "(ops.)" : "*"}</Label><DatePickerTR testid="user-hire-date" value={form.hire_date} onChange={(v) => setForm({ ...form, hire_date: v })} placeholder="Tarih seçin" /></div>
              </div>
              )}
            </div>
            </>)}
            {editing?.staff_code && <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">Personel Kodu: <b className="font-mono text-slate-700">{editing.staff_code}</b></div>}
            {canFull && (<div>
              <div className="flex items-center justify-between">
                <Label>Kademe / Seviye</Label>
                {can("role.manage") && (
                  <button type="button" data-testid="tiers-manage-btn" onClick={openTierMgr}
                    className="text-[11px] font-semibold text-brand-green hover:underline">Kademeleri Yönet</button>
                )}
              </div>
              <div className="relative mt-1">
                <select data-testid="user-tier-select" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  <option value="">Otomatik (role göre)</option>
                  {userTiers.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Kullanıcının kurumsal kademesi (Üst/Orta/Alt Düzey vb.). Boş bırakılırsa role göre otomatik atanır.</p>
            </div>)}
            <div>
              <Label>Lokasyon(lar) {isGlobalRole(form.role) ? "" : "*"}</Label>
              {!canFull ? (
                <p data-testid="user-location-readonly" className="mt-1 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
                  {isGlobalRole(form.role) ? "Global — tüm lokasyonlar" : (form.location_ids.map((id) => (locations.find((l) => l.id === id) || {}).name).filter(Boolean).join(", ") || "—")} <span className="text-slate-400">(sabit)</span>
                </p>
              ) : isGlobalRole(form.role) ? (
                <p className="mt-1 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
                  Bu rol <b>global</b> erişime sahiptir; tüm lokasyonlarda çalışır.
                </p>
              ) : (
                <div className="mt-1 rounded-md border border-input">
                  <div className="flex items-center gap-2 border-b border-slate-100 p-2">
                    <Input data-testid="user-loc-search" value={locQuery} onChange={(e) => setLocQuery(e.target.value)} placeholder="Lokasyon ara…" className="h-8 flex-1" disabled={isAllLoc} />
                    <span className="shrink-0 text-[11px] font-semibold text-slate-400" data-testid="user-loc-count">{isAllLoc ? "Tümü" : form.location_ids.length} seçili</span>
                  </div>
                  <label data-testid="user-location-global" className="flex cursor-pointer items-center gap-2 border-b border-slate-100 bg-slate-50 px-2.5 py-2 text-sm">
                    <input type="checkbox" checked={isAllLoc} onChange={toggleAllLoc} className="h-4 w-4 rounded border-slate-300 accent-brand-green" />
                    <span className="font-semibold text-slate-700">Global — Tüm Lokasyonlar</span>
                    <span className="text-[10px] text-slate-400">(seçilirse tek tek seçime gerek yok)</span>
                  </label>
                  <div data-testid="user-locations" className="max-h-44 space-y-0.5 overflow-auto p-2">
                    {locationOptions.length === 0 && <p className="px-1 text-xs text-slate-400">Atanabilir lokasyon yok.</p>}
                    {locationOptions
                      .filter((l) => { const t = locQuery.trim().toLocaleLowerCase("tr"); return !t || (l.name || "").toLocaleLowerCase("tr").includes(t); })
                      .slice(0, 300)
                      .map((l) => (
                      <label key={l.id} data-testid={`user-location-opt-${l.id}`}
                        className={`flex items-center gap-2 rounded-sm px-1.5 py-1 text-sm ${isAllLoc ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-slate-50"}`}>
                        <input type="checkbox" disabled={isAllLoc} checked={isAllLoc || form.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} className="h-4 w-4 rounded border-slate-300" />
                        <span className="text-slate-700">{l.name}</span>
                        {l.type_label && <span className="text-[10px] text-slate-400">({l.type_label})</span>}
                      </label>
                    ))}
                    {locationOptions.filter((l) => { const t = locQuery.trim().toLocaleLowerCase("tr"); return !t || (l.name || "").toLocaleLowerCase("tr").includes(t); }).length > 300 && (
                      <p className="px-1 py-1 text-[11px] text-slate-400">İlk 300 lokasyon gösteriliyor — aramayı daraltın.</p>
                    )}
                  </div>
                </div>
              )}
              {isStoreManager && <p className="mt-1 text-[11px] text-slate-400">Yalnızca kendi lokasyonlarınızı atayabilirsiniz.</p>}
            </div>
            {canFull && sectorOpts.length > 0 && (
              <div>
                <div className="flex items-center justify-between">
                  <Label>Yönetim Sektörleri</Label>
                  <span className="text-[11px] font-semibold text-slate-400" data-testid="user-sectors-count">{(form.sectors || []).length} seçili</span>
                </div>
                <p className="mb-1 mt-1 text-[11px] text-slate-400">Kullanıcının giriş yapabileceği sektör panelleri. {isAdmin ? "Yönetici her sektöre tek hesapla erişir." : "Boş bırakılırsa sektör filtresi uygulanmaz (yetkilerine göre tüm modülleri görür)."}</p>
                <div className="rounded-md border border-input">
                  <div className="flex items-center gap-2 border-b border-slate-100 p-2">
                    <Input data-testid="user-sectors-search" value={sectorQuery} onChange={(e) => setSectorQuery(e.target.value)} placeholder="Sektör ara…" className="h-8 flex-1" />
                    {(form.sectors || []).length > 0 && (
                      <button type="button" data-testid="user-sectors-clear" onClick={() => setForm((f) => ({ ...f, sectors: [] }))}
                        className="shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50">Temizle</button>
                    )}
                  </div>
                  <div data-testid="user-sectors" className="flex max-h-44 flex-wrap content-start gap-1.5 overflow-auto p-2">
                    {sectorOpts
                      .filter((s) => { const t = sectorQuery.trim().toLocaleLowerCase("tr"); return !t || (s.name || "").toLocaleLowerCase("tr").includes(t) || (s.code || "").toLowerCase().includes(t); })
                      .map((s) => {
                        const on = (form.sectors || []).includes(s.code);
                        return (
                          <button key={s.code} type="button" data-testid={`user-sector-opt-${s.code}`} onClick={() => toggleSector(s.code)}
                            className={`inline-flex h-fit items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color || "#94a3b8" }} /> {s.name}
                          </button>
                        );
                      })}
                    {sectorOpts.filter((s) => { const t = sectorQuery.trim().toLocaleLowerCase("tr"); return !t || (s.name || "").toLocaleLowerCase("tr").includes(t) || (s.code || "").toLowerCase().includes(t); }).length === 0 && (
                      <p className="px-1 py-2 text-xs text-slate-400">Aramanızla eşleşen sektör yok.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {can("role.manage") && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="user-perm-overrides">
                <button type="button" data-testid="user-perm-toggle" onClick={() => setShowPerms((s) => !s)} className="flex w-full items-center justify-between text-sm font-semibold text-slate-700">
                  <span>Kişiye Özel Yetki &amp; Görünürlük{(form.extra_permissions.length + form.denied_permissions.length + Object.keys(form.module_overrides || {}).length) > 0 && <span className="ml-2 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-bold text-brand-orange">{form.extra_permissions.length} ek · {form.denied_permissions.length} kısıt · {Object.keys(form.module_overrides || {}).length} görünürlük</span>}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showPerms ? "rotate-180" : ""}`} />
                </button>
                {showPerms && (
                  <div className="mt-2 space-y-2">
                    {roleBase.includes("*") ? (
                      <p className="text-xs text-slate-500">Yönetici rolü tüm yetkilere sahiptir; kişiye özel düzenleme uygulanmaz.</p>
                    ) : (
                      <>
                        <p className="text-[11px] text-slate-400">Tek ağaçta yönetin: her modülde <b className="text-sky-600">Menüde/Gizle</b> görünürlüğünü ayarlayın, <b className="text-emerald-700">İşlemler</b>de rol dışı yetki <b>verin</b> ya da roldeki yetkiyi <b>kısıtlayın</b>, alt-sekmeleri açıp kapatın. Değişiklikler yalnızca bu kişiye özeldir; rolü etkilemez.</p>
                        <Input data-testid="user-perm-search" value={userTreeQuery} onChange={(e) => setUserTreeQuery(e.target.value)} placeholder="Modül / yetki / sekme ara…" className="h-9" />
                        <div className="max-h-[440px] overflow-auto rounded-lg border border-slate-200 bg-white p-2" data-testid="user-perm-tree-scroll">
                          <PermissionTree tree={permTree} query={userTreeQuery}
                            renderModuleVis={(m) => visSeg(m.key)}
                            renderPerm={(p) => permSeg(p)}
                            renderSubtab={(s) => visSeg(s.key, s.label)} />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {editing && canSecurity && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="user-security-section">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-brand-green" /> Güvenlik — Şifre & 2FA</p>
                <div className="rounded-md bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-600 ring-1 ring-inset ring-slate-200">
                  Şifreler güvenlik gereği geri döndürülemez şekilde saklanır; <b>eski şifre görüntülenemez</b>. Aşağıdaki buton kullanıcıya <b>şifre sıfırlama e-postası</b> gönderir — kullanıcı e-postadaki adımlarla kendi yeni şifresini belirler ve mevcut oturumları sonlanır.
                  <button type="button" data-testid="user-send-reset-email" disabled={resetBusy} onClick={sendResetEmail}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50">
                    {resetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Şifre Sıfırla (E-posta Gönder)
                  </button>
                </div>
                <div data-testid="user-2fa-manage">
                  <p className="mb-1.5 text-xs font-semibold text-slate-600">İki Adımlı Doğrulama (2FA) — durum:{" "}
                    <span className={twofa.enabled ? "text-emerald-600" : "text-slate-400"}>
                      {twofa.enabled ? `Açık · ${methodLabel(twofa.method)}${twofa.forced ? " · Zorunlu" : ""}` : "Kapalı"}
                    </span>
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[["off", "Kapalı"], ["email", "E-posta"], ["sms", "SMS"], ["both", "Her İkisi"]].map(([k, lbl]) => {
                      const activeSel = k === "off" ? !twofa.enabled : (twofa.enabled && twofa.method === k);
                      return (
                        <button key={k} type="button" data-testid={`user-2fa-${k}`} disabled={twofaBusy}
                          onClick={() => applyTwofa(k === "off" ? { enabled: false, method: twofa.method, forced: false } : { enabled: true, method: k, forced: twofa.forced })}
                          className={`rounded-md py-2 text-xs font-bold transition-colors disabled:opacity-50 ${activeSel ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{lbl}</button>
                      );
                    })}
                  </div>
                  {twofa.enabled && (
                    <label className="mt-2 flex items-center gap-2 text-xs text-slate-600" data-testid="user-2fa-forced-label">
                      <input type="checkbox" data-testid="user-2fa-forced" checked={twofa.forced} disabled={twofaBusy}
                        onChange={(e) => applyTwofa({ enabled: true, method: twofa.method, forced: e.target.checked })} className="h-4 w-4 accent-brand-green" />
                      Zorunlu kıl (kullanıcı kendisi kapatamaz)
                    </label>
                  )}
                  <p className="mt-1.5 text-[11px] text-slate-400">İşten ayrılan / 2FA erişimini kaybeden personel için "Kapalı" seçerek doğrulamayı iptal edin.</p>
                </div>
              </div>
            )}
            {me?.role === "assistant" && (
              <p data-testid="assistant-approval-note" className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">
                Yönetici Asistanı olarak yaptığınız değişiklikler önce diğer asistanların oylamasına, çoğunluk sağlanırsa Yönetici onayına gönderilir.
              </p>
            )}
            <Button data-testid="user-submit" onClick={save} className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {editing ? "Kaydet" : "Oluştur"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)}
        onDetected={(code) => { setForm((f) => ({ ...f, card_code: code })); setScanOpen(false); toast.success("Kart kodu okundu"); }} />
      <BulkDeleteDialog open={bulkOpen} count={selectedIds.length} label="kullanıcı" busy={bulkBusy} onClose={() => setBulkOpen(false)} onConfirm={doBulkDelete} />
      <BulkPinDialog open={bulkPinOpen} onClose={() => setBulkPinOpen(false)} onDone={load} />
      <NewStaffDialog open={newStaffOpen} onClose={() => setNewStaffOpen(false)} onCreated={onStaffCreated} />
      <BulkUserImportDialog open={bulkImportOpen} onClose={() => setBulkImportOpen(false)} onDone={() => { load(); loadStaff(); }} />

      <Dialog open={tierMgrOpen} onOpenChange={setTierMgrOpen}>
        <DialogContent data-testid="tiers-manage-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Kademe / Seviye Yönetimi</DialogTitle>
            <DialogDescription>Kurumsal kademeleri ekleyin, yeniden adlandırın, silin veya <b>yukarı/aşağı</b> taşıyarak sıralayın. En üstteki kademe en kıdemlidir. Bu liste kullanıcı formunda ve filtrelerde aynı sırayla görünür.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {tierDraft.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`tier-draft-row-${i}`}>
                <div className="flex shrink-0 flex-col">
                  <button type="button" data-testid={`tier-up-${i}`} disabled={i === 0} onClick={() => moveTier(i, -1)}
                    className="rounded-t-md px-1.5 py-0.5 text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" data-testid={`tier-down-${i}`} disabled={i === tierDraft.length - 1} onClick={() => moveTier(i, 1)}
                    className="-mt-px rounded-b-md px-1.5 py-0.5 text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <span className="w-5 shrink-0 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                <Input data-testid={`tier-draft-input-${i}`} value={t.label} placeholder="Kademe adı (ör. Üst Düzey)"
                  onChange={(e) => setTierDraft((d) => d.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-9 flex-1" />
                <button type="button" data-testid={`tier-draft-del-${i}`} onClick={() => setTierDraft((d) => d.filter((_, j) => j !== i))}
                  className="shrink-0 rounded-md p-2 text-red-500 ring-1 ring-inset ring-red-200 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {tierDraft.length === 0 && <p className="text-xs text-slate-400">Henüz kademe yok. Aşağıdan ekleyin.</p>}
            <Button type="button" variant="outline" data-testid="tier-draft-add" onClick={() => setTierDraft((d) => [...d, { code: "", label: "" }])}
              className="w-full border-dashed border-slate-300 text-slate-600"><UserPlus className="mr-1.5 h-4 w-4" /> Kademe Ekle</Button>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="outline" data-testid="tiers-cancel" onClick={() => setTierMgrOpen(false)}>Vazgeç</Button>
            <Button type="button" data-testid="tiers-save" disabled={tierBusy} onClick={saveTiers} className="bg-brand-orange text-white hover:bg-orange-600">
              {tierBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {credsInfo && (
        <Dialog open onOpenChange={() => setCredsInfo(null)}>
          <DialogContent data-testid="creds-dialog" className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Personel Giriş Bilgileri</DialogTitle>
              <DialogDescription>Bu bilgileri <b>{credsInfo.name}</b> adlı personele iletin. Personel <b>Firma + Kullanıcı Adı + Şifre</b> ile giriş yapar.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2"><span className="text-slate-500">Kullanıcı Adı</span><b data-testid="creds-username" className="font-mono">{credsInfo.username}</b></div>
              {credsInfo.password && <div className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 ring-1 ring-inset ring-amber-200"><span className="text-amber-700">Geçici Şifre</span><b data-testid="creds-password" className="font-mono">{credsInfo.password}</b></div>}
              {credsInfo.pin && <div className="flex items-center justify-between rounded-md bg-sky-50 px-3 py-2 ring-1 ring-inset ring-sky-200"><span className="text-sky-700">POS PIN</span><b data-testid="creds-pin" className="font-mono tracking-widest">{credsInfo.pin}</b></div>}
              <p className="text-[11px] text-slate-400">İlk girişte şifre değiştirilmesini önerin. Şifre sıfırlama personelin e-postasına gönderilir.</p>
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {credsInfo.phone && (
                <Button variant="outline" data-testid="creds-whatsapp" onClick={() => { const digits = (credsInfo.phone || "").replace(/\D/g, "").replace(/^0/, "90"); if (!digits) return toast.error("Telefon numarası yok"); const msg = `Groot ONE giriş bilgileriniz:\nKullanıcı Adı: ${credsInfo.username}${credsInfo.password ? `\nŞifre: ${credsInfo.password}` : ""}${credsInfo.pin ? `\nPIN: ${credsInfo.pin}` : ""}`; window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank"); }} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp</Button>
              )}
              {credsInfo.email && (
                <Button variant="outline" data-testid="creds-email" onClick={async () => { try { await api.post("/users/send-credentials", { email: credsInfo.email, name: credsInfo.name, username: credsInfo.username, password: credsInfo.password, pin: credsInfo.pin }); toast.success("Giriş bilgileri e-postayla gönderildi"); } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Gönderilemedi"); } }} className="border-sky-300 text-sky-700 hover:bg-sky-50"><Mail className="mr-1 h-4 w-4" /> E-posta Gönder</Button>
              )}
              <Button variant="outline" data-testid="creds-copy" onClick={() => { try { navigator.clipboard.writeText(`Kullanıcı Adı: ${credsInfo.username}${credsInfo.password ? `\nŞifre: ${credsInfo.password}` : ""}${credsInfo.pin ? `\nPIN: ${credsInfo.pin}` : ""}`); toast.success("Kopyalandı"); } catch (e) {} }}>Kopyala</Button>
              <Button data-testid="creds-ok" onClick={() => setCredsInfo(null)} className="bg-brand-green text-white hover:bg-brand-green-600">Tamam</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ShiftSlotEditor({ sk, label, value, onChange, staffDir, reqPinLen, editing }) {
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const v = value || {};
  const selected = (staffDir || []).find((s) => s.id === v.personnel_id);
  const opts = (staffDir || [])
    .filter((s) => !s.linked || s.id === v.personnel_id)
    .filter((s) => { const t = (q || "").toLowerCase().trim(); return !t || (s.name || "").toLowerCase().includes(t) || (s.code || "").toLowerCase().includes(t); })
    .slice(0, 50);
  return (
    <div className={`rounded-lg border p-3 ${v.personnel_id ? "border-brand-green/40 bg-brand-green/5" : "border-slate-200"}`} data-testid={`shift-slot-${sk}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{label} Vardiyası</span>
        {v.personnel_id && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <input type="checkbox" data-testid={`shift-active-${sk}`} checked={v.active !== false} onChange={(e) => onChange({ ...v, active: e.target.checked })} className="h-3.5 w-3.5 accent-brand-green" /> Aktif
          </label>
        )}
      </div>
      <div className="relative">
        <Input data-testid={`shift-personnel-${sk}`} value={selected ? `${selected.code} — ${selected.name}` : q}
          onChange={(e) => { setQ(e.target.value); onChange({ ...v, personnel_id: "" }); setOpenList(true); }}
          onFocus={() => setOpenList(true)} onBlur={() => setTimeout(() => setOpenList(false), 180)}
          placeholder="Personel ara (boş = bu vardiya yok)" autoComplete="off" />
        {openList && (
          <div className="absolute z-30 mt-1 max-h-44 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`shift-personnel-options-${sk}`}>
            {opts.map((s) => (
              <button key={s.id} type="button" data-testid={`shift-personnel-opt-${sk}-${s.id}`} onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange({ ...v, personnel_id: s.id }); setQ(""); setOpenList(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-brand-green/5">
                <span className="font-mono text-xs text-slate-400">{s.code}</span><span className="font-medium text-slate-700">{s.name}</span>
              </button>
            ))}
            {opts.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Eşleşen personel yok.</p>}
          </div>
        )}
      </div>
      {v.personnel_id && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Input data-testid={`shift-password-${sk}`} type="text" autoComplete="new-password" value={v.password || ""}
            onChange={(e) => onChange({ ...v, password: e.target.value })} placeholder={editing ? "Şifre (boş=değişmez)" : "Şifre"} />
          <Input data-testid={`shift-pin-${sk}`} inputMode="numeric" maxLength={reqPinLen} value={v.pin || ""}
            onChange={(e) => onChange({ ...v, pin: e.target.value.replace(/\D/g, "").slice(0, reqPinLen) })}
            placeholder={editing ? "PIN (boş=değişmez)" : `PIN ${reqPinLen} hane`} className="tabular" />
        </div>
      )}
    </div>
  );
}
